<template>
  <div
    :class="{ 'card': !dialog }"
  >
    <div :style="`height: ${minHeight}px;`">
      <v-layout
        class="card__header"
        data-id="cardHeader"
        align-center
      >
        <h2
          class="card__title"
          :class="{ 'card__title--dialog': dialog  }"
        >
          {{ !dialog ? $t(`${namespace}.ridePerformance`) : $t(`${namespace}.performanceChart`) }}
        </h2>
        <div :class="{ 'card__dropdown': !dialog }" v-if="isSuccess">
          <v-menu
            v-model="menuContainer"
            :close-on-content-click="false"
            :open-on-click="false"
            offset-y
            left
          >
            <v-btn slot="activator" flat @click="toggleDropdown">
              <span class="toolbar__button-label">
                <span v-if="category">{{ category }}</span>
                <span v-else>{{ ridesListFilter[0].text }}</span>
              </span>

              <v-icon
                v-if="!isSelected"
                class="autocomplete__arrow"
                color="accent"
                right
                small
              >
                keyboard_arrow_down
              </v-icon>

              <v-icon
                v-if="isSelected"
                class="autocomplete__arrow"
                id="menu-item__clear"
                color="accent"
                right
                small
                @click="toggleDropdown"
              >
                close
              </v-icon>
            </v-btn>

            <v-list class="v-list--custom">
              <v-list-tile
                v-for="category in ridesListFilter"
                :key="category.value"
                @click="getSelectedCategory(category)"
              >
                <v-list-tile-title>{{ category.text }}</v-list-tile-title>
              </v-list-tile>
            </v-list>
          </v-menu>
        </div>
      </v-layout>

      <div v-if="isSuccess">
        <div class="card__body">
          <canvas ref="chart" height="53" :style="`height: ${cardBodyHeight - 32.5}px;`" />
        </div>
      </div>

      <v-layout
        v-if="isFailed"
        class="card__content text-xs-center"
        :style="`height: ${cardBodyHeight}px;`"
        align-center
        justify-center
        fill-height
        row
      >
        <span>
          {{ $t('Common.genericErrorMsg') }}<br>
          <a
            href="#"
            :title="$t('Common.tryAgain')"
            class="text--link accent--text"
            @click.prevent="getData"
          >
            {{ $t('Common.tryAgain') }}.
          </a>
        </span>
      </v-layout>

      <v-layout
        v-if="isLoading"
        :style="`height: ${cardBodyHeight}px;`"
        class="card__content text-xs-center"
        fill-height
        align-center
        justify-center
        row
      >
        <v-progress-circular indeterminate color="accent" />
      </v-layout>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import Chart from 'chart.js'
import { convertToCurrencyBRL, formatNumber, isObject } from 'utils/functions'
import moment from 'moment'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'

export default {
  name: 'PerformanceChart',
  props: {
    filters: {
      type: Object,
      default: () => {}
    },
    minHeight: {
      type: Number,
      default: 0
    },
    dialog: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapState({
      company: state => state.company.selectedCompany
    }),

    currentLocale () {
      if (!this.$i18n) return
      return this.$i18n.locale.split('_').join('-')
    },

    routeName () {
      return this.$route.name
    },

    isLoading () {
      return this.currentStatus === STATUS_IS_LOADING
    },

    isSuccess () {
      return this.currentStatus === STATUS_SUCCESS
    },

    isFailed () {
      return this.currentStatus === STATUS_FAILED
    },

    cardBodyHeight () {
      const cardHeader = document.querySelector('[data-id="cardHeader"]')
      return !cardHeader ? this.minHeight : this.minHeight - cardHeader.offsetHeight
    }
  },
  data () {
    return {
      currentStatus: STATUS_IS_LOADING,
      namespace: 'Components.PerformanceChart',
      menuContainer: false,
      category: null,
      isSelected: false,
      ridesListFilter: this.$t('Components.PerformanceChart.ridesListFilter'),
      chart: null,
      activeOption: 'amount',
      data: {
        amount: [],
        averageFare: [],
        date: [],
        totalFare: []
      },
      startDate: null,
      endDate: null,
      preventReload: false
    }
  },
  methods: {
    isValidObject (val) {
      return isObject(val)
    },

    resetProps () {
      this.menuContainer = false
      this.isSelected = false
      this.category = null
      this.activeOption = 'amount'
      this.forceRenderChart()
    },

    toggleDropdown ($event) {
      if ($event.target.id === 'menu-item__clear') {
        this.resetProps()
        return
      }
      this.menuContainer = true
    },

    getSelectedCategory ({ value, text }) {
      this.activeOption = value
      this.menuContainer = false
      this.isSelected = true
      this.category = text
    },

    formatDate (str) {
      const arr = str.split('-')
      return `${arr[2]}/${arr[1]}/${arr[0]}`
    },

    formatMomentDate (str) {
      const arr = str.split('/')
      return `${arr[2]}-${arr[1]}-${arr[0]}`
    },

    getPointDate (str) {},

    renderChart () {
      const chartElement = this.$refs.chart
      if (this.isValidObject(this.data) && chartElement) {
        const vm = this
        this.chart = new Chart(chartElement, {
          type: 'bar',
          data: {
            labels: vm.data.date,
            datasets: [{
              data: vm.data[vm.activeOption],
              backgroundColor: '#1bbdfe',
              borderColor: '#1bbdfe',
              totalFare: vm.data.totalFare,
              averageFare: vm.data.averageFare
            }]
          },
          options: {
            maintainAspectRatio: false,
            legend: {
              display: false
            },
            title: {
              display: false
            },
            tooltips: {
              mode: 'index',
              intersect: false,
              displayColors: false,
              callbacks: {
                title: (tooltipItems, data) => {
                  moment.locale(this.currentLocale)
                  const label = tooltipItems[0].xLabel
                  const date = label
                  const day = moment(vm.formatMomentDate(date)).format('dddd')
                  return `${day}, ${label}`
                },

                label: (tooltipItem, data) => {
                  const index = tooltipItem.datasetIndex
                  const item = data.datasets[index]
                  const tooltipItemIndex = tooltipItem.index
                  const rides = item.data[tooltipItemIndex]

                  if (vm.activeOption === 'amount') {
                    return formatNumber(rides)
                  }

                  if (vm.activeOption === 'totalFare') {
                    return convertToCurrencyBRL(rides)
                  }

                  if (vm.activeOption === 'averageFare') {
                    return convertToCurrencyBRL(rides)
                  }
                }
              }
            },
            responsive: true,
            scales: {
              xAxes: [{ stacked: true }],
              yAxes: [{ stacked: true }]
            }
          }
        })
      }
    },

    async getData () {
      if (this.preventReload) return

      this.preventReload = true
      this.currentStatus = STATUS_IS_LOADING

      try {
        const startDate = this.filters.startDate || null
        const endDate = this.filters.endDate || null
        const companyId = this.company ? this.company.id : null
        const { data: res } = await this.$apiService.getChart(
          { startDate },
          { endDate },
          { companyId }
        )

        this.data.amount = []
        this.data.averageFare = []
        this.data.date = []
        this.data.totalFare = []

        if (this.isValidObject(res.data)) {
          this.$emit('categoriesPercent', res.data.categories)
          this.data.amount = res.data.series.map(item => item.amount)
          this.data.averageFare = res.data.series.map(item => item.averageFare)
          this.data.date = res.data.series.map(item => this.formatDate(item.date))
          this.data.totalFare = res.data.series.map(item => item.totalFare)
          window.setTimeout(() => { this.renderChart() }, 100)
          this.currentStatus = STATUS_SUCCESS
        } else {
          this.currentStatus = STATUS_FAILED
        }
      } catch (err) {
        console.log('Error', err)
        this.currentStatus = STATUS_FAILED
      }

      setTimeout(() => { this.preventReload = false }, 1000)
    },

    forceRenderChart () {
      this.currentStatus = STATUS_IS_LOADING
      window.setTimeout(() => {
        this.currentStatus = STATUS_SUCCESS
        window.setTimeout(() => { this.renderChart() }, 200)
      }, 400)
    }
  },
  watch: {
    activeOption () {
      this.forceRenderChart()
    },
    filters: {
      deep: true,
      handler () {
        this.getData()
      }
    },
    company (from, to) {
      this.getData()
    }
  },
  mounted () {
    // if (this.routeName === 'Reports') {
    this.getData()
    // }
  }
}
</script>

<style lang="scss" scoped>
.card {
  background-color: #fff;
  margin: 12px;
}

.card__content,
.card__body {
  min-height: 211px;
}

.card__header {
  height: 48px;
}

.card__body {
  overflow: hidden;
}

.card__title--dialog {
  font-size: 18px;
  padding-right: 0;
}

.card__dropdown {

  .v-btn {
    margin-left: 0;
    margin-right: 0;
    padding-left: 0;
    padding-right: 0;
  }
}
</style>
